import React, { useState, useEffect, useRef, useContext } from "react";
import { AuthContext } from "../../context/AuthContext";
import { useBlacTokenManager } from "../../apis/blacApi";
import { NavbarBlack } from "../../components/NavbarBlack/NavbarBlack";
import { Footer } from "../../components/Footer/Footer";

const WelcomeBlack: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const authContext = useContext(AuthContext);
  const { startTokenRefresh, stopTokenRefresh } = useBlacTokenManager();
  const [tokenRefreshInterval, setTokenRefreshInterval] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (containerRef.current) {
        const x = (e.clientX / window.innerWidth) * 2 - 1;
        const y = (e.clientY / window.innerHeight) * 2 - 1;
        
        containerRef.current.style.transform = `perspective(1000px) rotateY(${x * 2}deg) rotateX(${-y * 2}deg)`;
      }
    };

    const handleMouseLeave = () => {
      if (containerRef.current) {
        containerRef.current.style.transform = 'perspective(1000px) rotateY(0deg) rotateX(0deg)';
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  // Iniciar el refresh automático del token de Blac
  useEffect(() => {
    if (authContext.blacAvailable && authContext.isLoggedIn) {
      const interval = startTokenRefresh();
      setTokenRefreshInterval(interval);

      return () => {
        if (interval) {
          stopTokenRefresh(interval);
        }
      };
    }
  }, [authContext.blacAvailable, authContext.isLoggedIn]); // Agregar dependencias específicas

  const styles = `
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }

    .welcome-page-black {
      font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      background: linear-gradient(135deg, #000000 0%, #0a0a0a 25%, #1a1a1a 50%, #0a0a0a 75%, #000000 100%);
      min-height: calc(100vh - 72px); /* Restar altura del navbar */
      color: #ffffff;
      overflow: hidden;
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;
      padding-top: 20px; /* Espacio adicional desde el navbar */
    }

    /* Animated background elements */
    .background-animation-black {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 1;
    }

    .floating-element-black {
      position: absolute;
      width: 4px;
      height: 4px;
      background: rgba(0, 122, 255, 0.3);
      border-radius: 50%;
      animation: floatBlackWelcome 15s infinite linear;
    }

    .floating-element-black:nth-child(1) { left: 10%; animation-delay: 0s; }
    .floating-element-black:nth-child(2) { left: 20%; animation-delay: 2s; }
    .floating-element-black:nth-child(3) { left: 30%; animation-delay: 4s; }
    .floating-element-black:nth-child(4) { left: 40%; animation-delay: 6s; }
    .floating-element-black:nth-child(5) { left: 50%; animation-delay: 8s; }
    .floating-element-black:nth-child(6) { left: 60%; animation-delay: 10s; }
    .floating-element-black:nth-child(7) { left: 70%; animation-delay: 12s; }
    .floating-element-black:nth-child(8) { left: 80%; animation-delay: 14s; }
    .floating-element-black:nth-child(9) { left: 90%; animation-delay: 1s; }

    @keyframes floatBlackWelcome {
      0% {
        transform: translateY(100vh) rotate(0deg);
        opacity: 0;
      }
      10% {
        opacity: 1;
      }
      90% {
        opacity: 1;
      }
      100% {
        transform: translateY(-100px) rotate(360deg);
        opacity: 0;
      }
    }

    /* Grid overlay */
    .grid-overlay-black {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-image: 
        linear-gradient(rgba(0, 122, 255, 0.03) 1px, transparent 1px),
        linear-gradient(90deg, rgba(0, 122, 255, 0.03) 1px, transparent 1px);
      background-size: 50px 50px;
      z-index: 2;
      animation: gridPulse 4s ease-in-out infinite;
    }

    @keyframes gridPulse {
      0%, 100% { opacity: 0.3; }
      50% { opacity: 0.1; }
    }

    /* Main container */
    .welcome-container-black {
      background: rgba(0, 0, 0, 0.8);
      backdrop-filter: blur(20px);
      border: 1px solid rgba(0, 122, 255, 0.2);
      border-radius: 30px;
      padding: 50px; /* Reducir padding para compensar el navbar */
      text-align: center;
      position: relative;
      z-index: 10;
      max-width: 800px;
      margin: 20px; /* Reducir margen */
      box-shadow: 
        0 25px 50px rgba(0, 0, 0, 0.5),
        0 0 0 1px rgba(0, 122, 255, 0.1),
        inset 0 1px 0 rgba(255, 255, 255, 0.1);
      transition: all 0.3s ease;
    }

    .welcome-container-black:hover {
      border-color: rgba(0, 122, 255, 0.4);
      box-shadow: 
        0 30px 60px rgba(0, 0, 0, 0.6),
        0 0 0 1px rgba(0, 122, 255, 0.2),
        inset 0 1px 0 rgba(255, 255, 255, 0.1);
    }

    /* Black branding */
    .black-branding {
      margin-bottom: 50px;
      animation: fadeInUp 1s ease-out 0.3s both;
    }

    .black-logo {
      font-size: 48px;
      font-weight: 700;
      color: #ffffff;
      letter-spacing: 12px;
      margin-bottom: 15px;
      position: relative;
    }

    .black-logo::after {
      content: '';
      position: absolute;
      bottom: -8px;
      left: 50%;
      transform: translateX(-50%);
      width: 80px;
      height: 3px;
      background: linear-gradient(90deg, transparent, #007AFF, transparent);
      animation: logoGlow 3s ease-in-out infinite alternate;
    }

    @keyframes logoGlow {
      from {
        box-shadow: 0 0 10px rgba(0, 122, 255, 0.5);
        width: 80px;
      }
      to {
        box-shadow: 0 0 30px rgba(0, 122, 255, 0.8);
        width: 120px;
      }
    }

    .system-title-black {
      font-size: 16px;
      color: #888888;
      font-weight: 400;
      letter-spacing: 4px;
    }

    /* Welcome content */
    .welcome-content-black {
      margin-bottom: 50px;
    }

    .welcome-title-black {
      font-size: 56px;
      font-weight: 300;
      margin-bottom: 25px;
      background: linear-gradient(135deg, #ffffff 0%, #007AFF 50%, #ffffff 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      animation: fadeInUp 1s ease-out 0.6s both;
      line-height: 1.2;
    }

    .welcome-subtitle-black {
      font-size: 20px;
      color: #aaaaaa;
      font-weight: 300;
      line-height: 1.6;
      margin-bottom: 15px;
      animation: fadeInUp 1s ease-out 0.9s both;
    }

    .system-description-black {
      font-size: 16px;
      color: #777777;
      font-weight: 300;
      letter-spacing: 1px;
      animation: fadeInUp 1s ease-out 1.2s both;
    }

    @keyframes fadeInUp {
      from {
        opacity: 0;
        transform: translateY(30px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    /* Enter button */
    .enter-system-black {
      display: inline-block;
      padding: 18px 40px;
      background: linear-gradient(135deg, #007AFF 0%, #0056CC 100%);
      border: 1px solid rgba(0, 122, 255, 0.3);
      border-radius: 50px;
      color: #ffffff;
      font-size: 16px;
      font-weight: 500;
      text-decoration: none;
      letter-spacing: 2px;
      cursor: pointer;
      transition: all 0.4s ease;
      position: relative;
      overflow: hidden;
      animation: fadeInUp 1s ease-out 1.5s both;
    }

    .enter-system-black::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
      transition: left 0.6s ease;
    }

    .enter-system-black:hover {
      background: linear-gradient(135deg, #0056CC 0%, #003D99 100%);
      transform: translateY(-3px);
      box-shadow: 0 15px 35px rgba(0, 122, 255, 0.4);
      border-color: rgba(0, 122, 255, 0.5);
    }

    .enter-system-black:hover::before {
      left: 100%;
    }

    .enter-system-black:active {
      transform: translateY(-1px);
    }

    /* Status indicator */
    .system-status-black {
      position: absolute;
      top: 20px; /* Reducir desde arriba */
      right: 30px;
      display: flex;
      align-items: center;
      gap: 10px;
      animation: fadeInUp 1s ease-out 1.8s both;
    }

    .status-indicator-black {
      width: 12px;
      height: 12px;
      border-radius: 50%;
      background: #00ff88;
      animation: pulse 2s infinite;
    }

    .status-indicator-black.blac-status {
      background: ${authContext.blacAvailable ? '#00ff88' : '#ff6b6b'};
    }

    @keyframes pulse {
      0% {
        box-shadow: 0 0 0 0 rgba(0, 255, 136, 0.7);
      }
      70% {
        box-shadow: 0 0 0 10px rgba(0, 255, 136, 0);
      }
      100% {
        box-shadow: 0 0 0 0 rgba(0, 255, 136, 0);
      }
    }

    .status-text-black {
      font-size: 12px;
      color: #888888;
      font-weight: 400;
    }

    /* Device counter integration */
    .device-counter-black {
      display: inline-block;
      font-size: 14px;
      color: #007AFF;
      margin-left: 10px;
      font-weight: 400;
      opacity: 0.8;
    }

    /* Responsive design */
    @media (max-width: 768px) {
      .welcome-container-black {
        margin: 20px;
        padding: 40px 30px;
      }
      
      .black-logo {
        font-size: 32px;
        letter-spacing: 8px;
      }
      
      .welcome-title-black {
        font-size: 42px;
      }
      
      .welcome-subtitle-black {
        font-size: 18px;
      }
      
      .system-description-black {
        font-size: 14px;
      }
      
      .system-status-black {
        position: static;
        justify-content: center;
        margin-top: 30px;
      }
    }

    @media (max-width: 480px) {
      .welcome-container-black {
        padding: 30px 20px;
      }
      
      .black-logo {
        font-size: 28px;
        letter-spacing: 6px;
      }
      
      .welcome-title-black {
        font-size: 36px;
      }
      
      .enter-system-black {
        padding: 15px 30px;
        font-size: 14px;
      }
    }
  `;

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <style dangerouslySetInnerHTML={{ __html: styles }} />
      
      {/* Navbar Black */}
      <NavbarBlack />
      
      <main style={{ flex: 1 }}>
        <div className="welcome-page-black">
          <div className="background-animation-black">
            <div className="floating-element-black"></div>
          <div className="floating-element-black"></div>
          <div className="floating-element-black"></div>
          <div className="floating-element-black"></div>
          <div className="floating-element-black"></div>
          <div className="floating-element-black"></div>
          <div className="floating-element-black"></div>
          <div className="floating-element-black"></div>
          <div className="floating-element-black"></div>
        </div>

        <div className="grid-overlay-black"></div>

        <div className="welcome-container-black" ref={containerRef}>
          <div className="system-status-black">
            <div className={`status-indicator-black ${authContext.blacAvailable ? 'blac-status' : ''}`}></div>
            <span className="status-text-black">
              Skyangel: {authContext.blacAvailable ? 'Conectado' : 'Desconectado'}
            </span>
          </div>

          <div className="black-branding">
            <div className="black-logo">Skyangel</div>
            <div className="system-title-black">Device Management</div>
          </div>

          <div className="welcome-content-black">
            <h1 className="welcome-title-black">Bienvenido</h1>
            <p className="welcome-subtitle-black">
              Sistema de Gestión de Dispositivos
            </p>
            <p className="system-description-black">
              Plataforma avanzada de monitoreo y control de dispositivos con tecnología de vanguardia
            </p>
          </div>
        </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default WelcomeBlack;
